import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: '0',
  },
  tableHeader: {
    borderBottom: '2px solid #1F2937',
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9CA3AF',
    backgroundColor: '#111827',
  },
  tableRow: {
    borderBottom: '1px solid #1F2937',
    transition: 'background-color 0.2s',
  },
  tableRowHover: {
    backgroundColor: '#0f172a',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#FFF',
  },
  serviceName: {
    fontWeight: '600',
    color: '#FFF',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeActive: {
    backgroundColor: '#10b981',
    color: '#fff',
  },
  badgeSuspended: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  badgePending: {
    backgroundColor: '#f59e0b',
    color: '#1f2937',
  },
  cost: {
    fontWeight: '600',
    color: '#3B82F6',
  },
  slaBadge: {
    backgroundColor: '#1F2937',
    color: '#3B82F6',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  contractDates: {
    fontSize: '13px',
    color: '#9CA3AF',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9CA3AF',
  },
};

export default function ClientServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/services');
        setServices(Array.isArray(data) ? data : data.services || []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load services');
        console.error('Services error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span style={{ ...styles.badge, ...styles.badgeActive }}>Active</span>;
      case 'suspended':
        return <span style={{ ...styles.badge, ...styles.badgeSuspended }}>Suspended</span>;
      case 'pending':
        return <span style={{ ...styles.badge, ...styles.badgePending }}>Pending</span>;
      default:
        return <span style={{ ...styles.badge }}>{status}</span>;
    }
  };

  if (loading) {
    return (
      <ClientShell title="Services">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading services...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Services">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  if (!services || services.length === 0) {
    return (
      <ClientShell title="Services">
        <div style={styles.emptyState}>
          <p>No services found</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Services">
      <div style={styles.container}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Service Name</th>
              <th style={styles.tableHeaderCell}>Status</th>
              <th style={styles.tableHeaderCell}>Monthly Cost</th>
              <th style={styles.tableHeaderCell}>SLA Tier</th>
              <th style={styles.tableHeaderCell}>Contract Period</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr
                key={service.id}
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={{ ...styles.tableCell, ...styles.serviceName }}>{service.name}</td>
                <td style={styles.tableCell}>{getStatusBadge(service.status)}</td>
                <td style={{ ...styles.tableCell, ...styles.cost }}>
                  ${service.monthlyCost?.toFixed(2) || '0.00'}
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.slaBadge}>{service.slaTier || 'Standard'}</span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.contractDates}>
                    {service.startDate} to {service.endDate}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ClientShell>
  );
}
